import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface EntitySearchResult {
  value: string;
  primary: string;
  secondary?: string | null;
  description?: string | null;
  note?: string | null;
  disabled?: boolean;
}

interface EntitySearchListProps {
  query: string;
  onQueryChange: (query: string) => void;
  results: EntitySearchResult[];
  onSelect: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  emptyMessage: string;
  isFetching: boolean;
  errorMessage?: string | null;
  minQueryLength?: number;
  showResults?: boolean;
}

/**
 * Shared server-backed account/party search used by portal impersonation and
 * license reassignment. APIs keep ownership of matching and result limits.
 */
export function EntitySearchList({
  query,
  onQueryChange,
  results,
  onSelect,
  placeholder,
  ariaLabel,
  emptyMessage,
  isFetching,
  errorMessage,
  minQueryLength = 2,
  showResults = true,
}: EntitySearchListProps) {
  const searchActive = query.trim().length >= minQueryLength;

  return (
    <div className="flex flex-col gap-2 text-left">
      <Input
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        aria-label={ariaLabel}
      />
      {showResults ? (
        <div className="max-h-64 overflow-y-auto rounded-md border bg-background">
          {isFetching && searchActive ? (
            <div className="p-3 text-sm text-muted-foreground">Searching…</div>
          ) : null}
          {!isFetching && errorMessage ? (
            <div className="p-3 text-sm text-destructive" role="alert">
              {errorMessage}
            </div>
          ) : null}
          {!isFetching &&
          !errorMessage &&
          searchActive &&
          results.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : null}
          <ul className="divide-y">
            {results.map((result) => (
              <li key={result.value}>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto w-full justify-start rounded-none py-3 text-left font-normal"
                  disabled={result.disabled}
                  onClick={() => onSelect(result.value)}
                >
                  <span className="block min-w-0 truncate">
                    <span className="font-medium">{result.primary}</span>
                    {result.secondary ? (
                      <span className="text-muted-foreground">
                        {" "}
                        · {result.secondary}
                      </span>
                    ) : null}
                    {result.description ? (
                      <span className="block text-xs text-muted-foreground">
                        {result.description}
                      </span>
                    ) : null}
                    {result.note ? (
                      <span className="block text-xs text-muted-foreground">
                        {result.note}
                      </span>
                    ) : null}
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
